import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import {
  Search,
  FileText,
  Building2,
  Users,
  Calendar,
  Receipt,
  Plus,
  ArrowRight,
  Clock,
  User,
  Briefcase,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GlobalSearch = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { isOfficeManager, isQuoter } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState({
    quotes: [],
    companies: [],
    employees: [],
    invoices: [],
  });
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const recent = localStorage.getItem("recentSearches");
    if (recent) {
      setRecentSearches(JSON.parse(recent));
    }
  }, []);

  // Keyboard shortcut to open
  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  // Search function with debounce
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults({ quotes: [], companies: [], employees: [], invoices: [] });
      return;
    }

    setLoading(true);
    try {
      const [quotesRes, companiesRes, employeesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/quotes?search=${encodeURIComponent(query)}`).catch(() => ({ data: [] })),
        axios.get(`${API}/companies`).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices`).catch(() => ({ data: [] })),
      ]);

      // Filter results client-side for companies, employees, invoices
      const q = query.toLowerCase();
      setResults({
        quotes: quotesRes.data.slice(0, 5),
        companies: companiesRes.data
          .filter((c) => c.name?.toLowerCase().includes(q))
          .slice(0, 5),
        employees: employeesRes.data
          .filter((e) => e.name?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q))
          .slice(0, 5),
        invoices: invoicesRes.data
          .filter((i) => i.invoice_number?.toLowerCase().includes(q) || i.company_name?.toLowerCase().includes(q))
          .slice(0, 5),
      });
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, performSearch]);

  const saveRecentSearch = (item) => {
    const recent = [item, ...recentSearches.filter((r) => r.id !== item.id)].slice(0, 5);
    setRecentSearches(recent);
    localStorage.setItem("recentSearches", JSON.stringify(recent));
  };

  const handleSelect = (type, item) => {
    saveRecentSearch({ ...item, type });
    setOpen(false);
    setSearch("");

    switch (type) {
      case "quote":
        navigate(`/quotes/${item.id}`);
        break;
      case "company":
        navigate(`/companies/${item.id}`);
        break;
      case "employee":
        navigate(`/employees/${item.id}`);
        break;
      case "invoice":
        navigate(`/invoices?id=${item.id}`);
        break;
      default:
        break;
    }
  };

  const quickActions = [
    { label: "New Quote", icon: Plus, action: () => navigate("/quotes/new"), roles: ["office_manager", "quoter"] },
    { label: "Add Company", icon: Building2, action: () => { sessionStorage.setItem("quickAction", "add-company"); navigate("/companies"); }, roles: ["office_manager"] },
    { label: "Add Employee", icon: Users, action: () => { sessionStorage.setItem("quickAction", "add-employee"); navigate("/employees"); }, roles: ["office_manager"] },
    { label: "Create Invoice", icon: Receipt, action: () => { sessionStorage.setItem("quickAction", "create-invoice"); navigate("/invoices"); }, roles: ["office_manager"] },
    { label: "View Schedule", icon: Calendar, action: () => navigate("/schedule"), roles: ["office_manager", "quoter", "employee"] },
  ];

  const hasResults = results.quotes.length > 0 || results.companies.length > 0 || results.employees.length > 0 || results.invoices.length > 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="global-search">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={() => setOpen(false)}
      />
      
      {/* Command Palette */}
      <div className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-2xl px-4">
        <Command className="rounded-xl border shadow-2xl bg-background overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center border-b px-4">
            <Search className="h-5 w-5 text-muted-foreground shrink-0" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search quotes, companies, employees, invoices..."
              className="flex-1 h-14 bg-transparent border-0 outline-none focus:ring-0 text-lg placeholder:text-muted-foreground"
              autoFocus
            />
            <kbd className="hidden sm:inline-flex h-6 select-none items-center gap-1 rounded border bg-muted px-2 font-mono text-xs text-muted-foreground">
              ESC
            </kbd>
          </div>

          <Command.List className="max-h-[400px] overflow-y-auto p-2">
            {loading && (
              <Command.Loading className="py-6 text-center text-muted-foreground">
                Searching...
              </Command.Loading>
            )}

            {!search && !hasResults && (
              <>
                {/* Quick Actions */}
                <Command.Group heading="Quick Actions">
                  {quickActions
                    .filter((action) => {
                      if (action.roles.includes("office_manager") && isOfficeManager) return true;
                      if (action.roles.includes("quoter") && isQuoter) return true;
                      if (action.roles.includes("employee")) return true;
                      return false;
                    })
                    .map((action) => (
                      <Command.Item
                        key={action.label}
                        onSelect={() => {
                          action.action();
                          setOpen(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <action.icon className="h-4 w-4 text-primary" />
                        <span>{action.label}</span>
                      </Command.Item>
                    ))}
                </Command.Group>

                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <Command.Group heading="Recent">
                    {recentSearches.map((item, idx) => (
                      <Command.Item
                        key={`recent-${idx}`}
                        onSelect={() => handleSelect(item.type, item)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{item.name || item.invoice_number || item.quote_number}</span>
                        <span className="text-xs text-muted-foreground ml-auto capitalize">{item.type}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
              </>
            )}

            {/* Search Results */}
            {search && hasResults && (
              <>
                {results.quotes.length > 0 && (
                  <Command.Group heading="Quotes">
                    {results.quotes.map((quote) => (
                      <Command.Item
                        key={`quote-${quote.id}`}
                        onSelect={() => handleSelect("quote", quote)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <FileText className="h-4 w-4 text-blue-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{quote.quote_number || quote.id}</p>
                          <p className="text-xs text-muted-foreground truncate">{quote.company_name}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {results.companies.length > 0 && (
                  <Command.Group heading="Companies">
                    {results.companies.map((company) => (
                      <Command.Item
                        key={`company-${company.id}`}
                        onSelect={() => handleSelect("company", company)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <Building2 className="h-4 w-4 text-violet-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{company.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{company.address}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {results.employees.length > 0 && (
                  <Command.Group heading="Employees">
                    {results.employees.map((employee) => (
                      <Command.Item
                        key={`employee-${employee.id}`}
                        onSelect={() => handleSelect("employee", employee)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <User className="h-4 w-4 text-emerald-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{employee.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{employee.email}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {results.invoices.length > 0 && (
                  <Command.Group heading="Invoices">
                    {results.invoices.map((invoice) => (
                      <Command.Item
                        key={`invoice-${invoice.id}`}
                        onSelect={() => handleSelect("invoice", invoice)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <Receipt className="h-4 w-4 text-teal-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{invoice.invoice_number}</p>
                          <p className="text-xs text-muted-foreground truncate">{invoice.company_name}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
              </>
            )}

            {search && !loading && !hasResults && (
              <Command.Empty className="py-6 text-center text-muted-foreground">
                No results found for &quot;{search}&quot;
              </Command.Empty>
            )}
          </Command.List>

          {/* Footer */}
          <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted">↑↓</kbd> Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted">↵</kbd> Select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-muted">⌘</kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-muted">K</kbd> to toggle
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
};

export default GlobalSearch;
